/**
 * VNode → Forme document node.
 *
 * Block renderers (built-in and custom alike) return a VNode tree, so a single
 * translator gives every block a browserless PDF path for free — no block has
 * to know Forme exists.
 *
 * Alongside each emitted node the translator records an `Annotation` in exactly
 * the same shape. That is what lets `measure.ts` map Forme's layout result back
 * onto BroadPaper node keys without the engine needing to carry our identifiers.
 */
import { classNames, styleString, toHtml, walkVNodes, type VNode } from "@broadpaper/core";
import { mapStyle, mapColumnWidth } from "@formepdf/shared";
import { parseDeclarations, translateStyle, length, pxToPt, type FormeStyle } from "./style.js";
import { splitSvg, svgFromDataUri, type SvgLabel } from "./svg.js";
import type { Annotation } from "./measure.js";

export interface FormeNode {
  kind: Record<string, unknown>;
  style: FormeStyle;
  children: FormeNode[];
  href?: string;
  alt?: string;
}

export interface TranslateContext {
  warnings: Set<string>;
  /**
   * Keeps design-mode affordances — placeholders, empty-slot outlines, the
   * page-break rule. They never belong in a PDF, but when this translation is
   * measuring the editor's canvas they are part of what the canvas draws, and
   * measuring them as nothing makes blocks invisible on screen.
   */
  design?: boolean;
  /**
   * Drops page-break nodes, for the passes where BroadPaper has already decided
   * the pages. Measurement uses one unbounded page and reads back only its
   * first, so a break there would silently discard everything after it; the
   * paginated draw pass has placed its content already and must not reflow.
   */
  suppressPageBreaks?: boolean;
}

/** An emitted node paired with its annotation. */
export interface Translated {
  node: FormeNode;
  ann: Annotation;
}

const INLINE_TAGS = new Set(["span", "strong", "b", "em", "i", "u", "s", "code", "a", "small"]);
const TEXT_BLOCK_TAGS = new Set(["p", "h1", "h2", "h3", "h4", "h5", "h6", "li", "blockquote", "figcaption", "label", "td", "th", "div"]);

function declarationsOf(v: VNode): Record<string, string> {
  return parseDeclarations(typeof v.props.style === "string" ? v.props.style : styleString(v.props.style));
}

function styleOf(v: VNode, ctx: TranslateContext): FormeStyle {
  const decls = declarationsOf(v);
  const { style, unsupported } = translateStyle(decls);
  for (const u of unsupported) ctx.warnings.add(`unsupported CSS property "${u}" (ignored)`);
  // Forme's own public mapper produces the engine JSON, so enum spellings and
  // dimension wrappers stay correct as the engine evolves.
  return mapStyle(style as never) as FormeStyle;
}

/** Flattens an element's descendants into a single string of text. */
function textOf(v: VNode | string): string {
  if (typeof v === "string") return v;
  if (v.tag === "br") return "\n";
  return v.children.map(textOf).join("");
}

function hasBlockChild(v: VNode): boolean {
  return v.children.some((c) => typeof c !== "string" && !INLINE_TAGS.has(c.tag) && c.tag !== "br");
}

/** True when this subtree holds wrapped body text whose line boxes we want. */
function holdsWrappedText(v: VNode): boolean {
  let found = false;
  walkVNodes(v, (n) => {
    if (n.props["data-part"] === "text") found = true;
    return !found;
  });
  return found;
}

function leaf(node: FormeNode, ann: Annotation = { children: [] }): Translated {
  return { node, ann };
}

function svgNode(v: VNode, ctx: TranslateContext): Translated {
  const width = Number(v.props.width) || 0;
  const height = Number(v.props.height) || 0;
  // The engine builds its own SVG viewport from width, height and view_box, so
  // `content` is the markup that goes inside it. Handing it a whole <svg>
  // element instead nests one viewport in another: the inner one keeps our
  // pixel dimensions inside an outer one sized in points, and every chart is
  // drawn a third too large and clipped by its box.
  const { markup, labels } = splitSvg(v, (message) => ctx.warnings.add(message));
  const viewBox = typeof v.props.viewBox === "string" ? v.props.viewBox : `0 0 ${width} ${height}`;
  // The size goes in the style as well as the kind. Without a box the engine
  // lays the node out as auto and it takes whatever room is going, which turns
  // a 19px trend arrow into a line of its own.
  const box = mapStyle({ width: pxToPt(width), height: pxToPt(height) } as never) as FormeStyle;
  const shapes: FormeNode = {
    kind: { type: "Svg", width: pxToPt(width), height: pxToPt(height), view_box: viewBox, content: markup },
    style: { ...box, ...styleOf(v, ctx) },
    children: []
  };
  if (!labels.length) return leaf(shapes);

  // The engine does not draw SVG text, so the labels are placed over the chart
  // as real text. Positions come from the view box, which is the coordinate
  // system the labels were written in.
  const vb = viewBox.trim().split(/[\s,]+/).map(Number);
  const sx = vb.length === 4 && vb[2] ? width / vb[2]! : 1;
  const sy = vb.length === 4 && vb[3] ? height / vb[3]! : 1;
  const children = [shapes, ...labels.map((l) => labelNode(l, sx, sy))];
  return {
    node: {
      kind: { type: "View" },
      // Relative, so the labels are placed against this box rather than the page.
      style: mapStyle({ position: "relative", width: pxToPt(width), height: pxToPt(height) } as never) as FormeStyle,
      children
    },
    ann: { children: children.map(() => ({ children: [] })) }
  };
}

/** One SVG label, positioned over the chart it was lifted out of. */
function labelNode(l: SvgLabel, sx: number, sy: number): FormeNode {
  const x = l.x * sx;
  const size = l.fontSize * Math.min(sx, sy);
  // SVG anchors text at a point; the engine positions a box. Without knowing
  // the shaped width we estimate it — 0.55em is close for the sans faces
  // reports use — and place the box so the anchor point lands where the SVG
  // asked. A box wide enough to never wrap but no wider keeps a label from
  // being pushed around inside a small graphic such as a logo.
  const estimate = Math.max(size, l.text.length * size * 0.55);
  const width = estimate * 1.5;
  const left = l.anchor === "middle" ? x - estimate / 2 : l.anchor === "end" ? x - estimate : x;
  const style: Record<string, unknown> = {
    position: "absolute",
    left: pxToPt(left),
    // `y` is a baseline; a box starts roughly one ascender higher.
    top: pxToPt(l.y * sy - size * 0.8),
    width: pxToPt(width),
    fontSize: pxToPt(size),
    textAlign: "left"
  };
  if (l.fill) style.color = l.fill;
  if (l.fontFamily) style.fontFamily = l.fontFamily.split(",")[0]!.trim().replace(/^['"]|['"]$/g, "");
  if (l.fontWeight) style.fontWeight = l.fontWeight;
  return { kind: { type: "Text", content: l.text }, style: mapStyle(style as never) as FormeStyle, children: [] };
}

/**
 * The size an inline SVG should be drawn at.
 *
 * An `<img>` is usually sized by CSS rather than by the artwork, so a logo
 * styled to 28px tall must be drawn at 28px tall and not at its intrinsic 48.
 * The aspect ratio comes from the artwork whenever only one dimension is given.
 */
function sizeFrom(img: VNode, svg: VNode): { width: number; height: number } {
  const own = { width: Number(svg.props.width) || 0, height: Number(svg.props.height) || 0 };
  const box = String(svg.props.viewBox ?? "").trim().split(/[\s,]+/).map(Number);
  const natural = {
    width: own.width || (box.length === 4 ? box[2]! : 0),
    height: own.height || (box.length === 4 ? box[3]! : 0)
  };
  const css = parseDeclarations(typeof img.props.style === "string" ? img.props.style : styleString(img.props.style));
  const asked = { width: Number.parseFloat(css.width ?? ""), height: Number.parseFloat(css.height ?? "") };
  const ratio = natural.width && natural.height ? natural.width / natural.height : 0;

  let width = Number.isFinite(asked.width) ? asked.width : 0;
  let height = Number.isFinite(asked.height) ? asked.height : 0;
  if (!width && height && ratio) width = height * ratio;
  if (!height && width && ratio) height = width / ratio;
  return { width: width || natural.width, height: height || natural.height };
}

function tableNode(v: VNode, ctx: TranslateContext): Translated {
  const columns: Array<{ width: unknown }> = [];
  const rows: FormeNode[] = [];
  const rowAnns: Annotation[] = [];
  const rowKinds: Array<"header" | "body" | "footer"> = [];

  const visit = (node: VNode, kind: "header" | "body" | "footer") => {
    for (const child of node.children) {
      if (typeof child === "string") continue;
      if (child.tag === "colgroup") {
        for (const col of child.children) {
          if (typeof col === "string") continue;
          const w = parseDeclarations(typeof col.props.style === "string" ? col.props.style : styleString(col.props.style)).width;
          const parsed = w ? length(w) : undefined;
          // Forme sizes table columns by fraction or fixed points; our percentage
          // widths become fractions of the table width.
          columns.push({
            width: mapColumnWidth(
              typeof parsed === "string" ? { fraction: Number.parseFloat(parsed) / 100 } : typeof parsed === "number" ? { fixed: parsed } : "auto"
            )
          });
        }
      } else if (child.tag === "thead") visit(child, "header");
      else if (child.tag === "tbody") visit(child, "body");
      else if (child.tag === "tfoot") visit(child, "footer");
      else if (child.tag === "tr") {
        const cells: FormeNode[] = [];
        const cellAnns: Annotation[] = [];
        for (const cell of child.children) {
          if (typeof cell === "string") continue;
          const inner = childNodes(cell, ctx);
          cells.push({
            kind: { type: "TableCell", col_span: Number(cell.props.colspan) || 1, row_span: Number(cell.props.rowspan) || 1 },
            style: styleOf(cell, ctx),
            children: inner.nodes
          });
          cellAnns.push({ children: inner.anns });
        }
        rows.push({ kind: { type: "TableRow", is_header: kind === "header" }, style: styleOf(child, ctx), children: cells });
        rowAnns.push({ children: cellAnns });
        rowKinds.push(kind);
      }
    }
  };
  visit(v, "body");
  return {
    node: { kind: { type: "Table", columns }, style: styleOf(v, ctx), children: rows },
    ann: { rowKinds, children: rowAnns }
  };
}

/** Style props that describe glyphs rather than the box around them. */
const TEXT_PROPS = ["color", "fontFamily", "fontSize", "fontWeight", "fontStyle", "lineHeight", "letterSpacing", "textAlign", "textTransform", "textDecoration"] as const;

function textStyleOf(style: FormeStyle): FormeStyle {
  const out: FormeStyle = {};
  for (const k of TEXT_PROPS) if (style[k] !== undefined) out[k] = style[k];
  return out;
}

function childNodes(v: VNode, ctx: TranslateContext, inherited: FormeStyle = {}): { nodes: FormeNode[]; anns: Annotation[] } {
  const nodes: FormeNode[] = [];
  const anns: Annotation[] = [];
  for (const child of v.children) {
    if (typeof child === "string") {
      if (!child.trim()) continue;
      // The engine's model is React-Native-flavoured: a view's font and colour
      // do not reach a text node inside it, so they are copied down.
      nodes.push({ kind: { type: "Text", content: child }, style: inherited, children: [] });
      anns.push({ children: [] });
      continue;
    }
    const t = translate(child, ctx);
    if (!t) continue;
    nodes.push(t.node);
    anns.push(t.ann);
  }
  return { nodes, anns };
}

/** Translates one VNode, returning the emitted node and its annotation. */
export function translate(v: VNode, ctx: TranslateContext): Translated | null {
  const cls = classNames(v.props.class);
  // Design-mode affordances never belong in a PDF, but they do belong in a
  // measurement of the design canvas, which draws them.
  if (!ctx.design && /bp-(placeholder|empty-section|empty-column)\b/.test(cls)) return null;
  if (/bp-page-break\b/.test(cls) && !ctx.suppressPageBreaks) return leaf({ kind: { type: "PageBreak" }, style: {}, children: [] });

  let result: Translated | null;
  switch (v.tag) {
    case "svg":
      result = svgNode(v, ctx);
      break;
    case "table":
      result = tableNode(v, ctx);
      break;
    case "img": {
      const src = typeof v.props.src === "string" ? v.props.src : "";
      if (!src) return null;
      // An SVG data URI is vector artwork, not a bitmap. The engine's image
      // decoder cannot read one and leaves a grey box where the logo should be,
      // so treat it as the drawing it is.
      const vector = svgFromDataUri(src);
      if (vector) {
        const sized: VNode = { ...vector, props: { ...vector.props, ...sizeFrom(v, vector) } };
        result = svgNode(sized, ctx);
        break;
      }
      const node: FormeNode = { kind: { type: "Image", src }, style: styleOf(v, ctx), children: [] };
      if (typeof v.props.alt === "string" && v.props.alt) node.alt = v.props.alt;
      result = leaf(node);
      break;
    }
    case "br":
      result = leaf({ kind: { type: "Text", content: "\n" }, style: {}, children: [] });
      break;
    case "ul":
    case "ol": {
      const items: FormeNode[] = [];
      const itemAnns: Annotation[] = [];
      for (const li of v.children) {
        if (typeof li === "string") continue;
        const inner = childNodes(li, ctx);
        items.push({ kind: { type: "ListItem" }, style: styleOf(li, ctx), children: inner.nodes });
        itemAnns.push({ children: inner.anns });
      }
      result = {
        // Marker names are the CSS `list-style-type` spellings, not capitalised
        // variant names: the engine rejects the whole document over one wrong
        // enum, so a single bullet list used to mean no PDF at all.
        node: { kind: { type: "List", ordered: v.tag === "ol", marker_type: v.tag === "ol" ? "decimal" : "disc", start: 1 }, style: styleOf(v, ctx), children: items },
        ann: { children: itemAnns }
      };
      break;
    }
    case "colgroup":
    case "col":
      return null;
    default:
      result = null;
  }

  if (!result) {
    const style = styleOf(v, ctx);
    const heading = /^h([1-6])$/.exec(v.tag);

    const content = textOf(v);
    const blank = !content.trim();
    if (blank && !Object.keys(style).length && !v.children.length) return null;

    // A text block with only inline content becomes one Text node; the inline
    // runs are flattened into it so their content survives.
    //
    // A text node with nothing in it is a different matter. Spacers and
    // dividers are empty `div`s carrying only a height or a border, and the
    // engine gives every Text node a line box — so translating them as text
    // would silently add a line's worth of height that no browser shows. They
    // become plain views instead. An intentionally empty paragraph keeps its
    // blank line, because there the line is the point.
    // An element that lays its children out — flex or grid — is a layout box
    // even when every child is an inline tag, or a bare string. Flattening one
    // into a run of text turns "Lower risk … Higher risk" into
    // "Lower riskHigher risk", and turns a callout's round badge into a text
    // node that paints neither its background nor its border.
    const decls = declarationsOf(v);
    const laysOut = decls.display === "flex" || decls.display === "inline-flex" || decls.display === "grid";
    const textual = (TEXT_BLOCK_TAGS.has(v.tag) || INLINE_TAGS.has(v.tag) || heading) && !hasBlockChild(v) && !laysOut;
    if (textual && (!blank || /bp-p--empty/.test(cls))) {
      const kind: Record<string, unknown> = heading ? { type: "Heading", level: Number(heading[1]), content } : { type: "Text", content };
      const node: FormeNode = { kind, style, children: [] };
      // A text node's link goes in its kind, not beside it. The engine reads a View's
      // link from the node and a Text's from `kind.href`, and an `href` beside a Text's
      // kind is ignored without a warning — so every link in text, the free plan's
      // credit line included, used to arrive in a tree that looked right and leave a
      // file with no link in it. Found by rendering both shapes and counting the link
      // annotations; `tests/pdf/watermark.test.ts` opens a finished file to hold it.
      if (v.tag === "a" && typeof v.props.href === "string") kind.href = v.props.href;
      result = leaf(node);
    } else {
      const inner = childNodes(v, ctx);
      // An empty view still draws when it carries a rule, a fill or a height —
      // that is exactly what our spacers and dividers are.
      const draws = Object.keys(style).some((k) => /^border[A-Za-z]*Width$/.test(k)) || style.backgroundColor !== undefined || style.height !== undefined;
      if (inner.nodes.length === 0 && !draws) return null;
      if (decls.display === "grid") applyGridAsFlex(style, inner.nodes, decls);
      const node: FormeNode = { kind: { type: "View" }, style, children: inner.nodes };
      if (v.tag === "a" && typeof v.props.href === "string") node.href = v.props.href;
      result = { node, ann: { children: inner.anns } };
    }
  }

  // Block wrappers carry `data-key`; that is our handle for mapping geometry back.
  const key = v.props["data-key"];
  if (typeof key === "string") {
    result.ann.key = key;
    if (holdsWrappedText(v)) result.ann.text = true;
    // A table sits inside its block's wrapper, so lift the row roles onto the
    // keyed node — that is where the paginator looks for them.
    if (!result.ann.rowKinds) {
      const rows = findRowKinds(result.ann);
      if (rows) result.ann.rowKinds = rows;
    }
  }
  return result;
}

/**
 * Reproduces one of our CSS grids with flexbox.
 *
 * Rows declare twelve tracks and their columns a span, which becomes a
 * proportional `flexGrow`. A grid whose children outnumber its tracks (the
 * key/value grid) genuinely wraps, so it keeps `flexWrap` and a per-track
 * basis; a row that fits on one line must not wrap, or an over-wide child such
 * as a logo pushes the next column onto a second line.
 */
function applyGridAsFlex(style: FormeStyle, children: FormeNode[], decls: Record<string, string>): void {
  const tracks = /repeat\(\s*(\d+)/.exec(decls["grid-template-columns"] ?? "");
  const spanned = children.filter((c) => typeof c.style.flexGrow === "number" && c.style.flexGrow > 0).length;
  const perRow = tracks && spanned === 0 ? Number(tracks[1]) : children.length;
  const wraps = children.length > perRow;
  style.flexWrap = wraps ? "Wrap" : "NoWrap";
  for (const child of children) {
    if (child.style.flexGrow === undefined) {
      child.style.flexGrow = 1;
      child.style.flexBasis = wraps ? { Percent: 100 / Math.max(1, perRow) } : { Pt: 0 };
    }
    // Columns must give way rather than overflow: a wide logo or a long word
    // should shrink its column, never push a sibling onto the next line.
    if (child.style.flexShrink === undefined) child.style.flexShrink = 1;
    if (child.style.minWidth === undefined) child.style.minWidth = { Pt: 0 };
  }
}

/** First `rowKinds` found in a subtree, so a wrapper can adopt its table's rows. */
function findRowKinds(ann: Annotation): Annotation["rowKinds"] | undefined {
  if (ann.rowKinds) return ann.rowKinds;
  for (const c of ann.children) {
    const found = findRowKinds(c);
    if (found) return found;
  }
  return undefined;
}

/** Translates one VNode. Returns null for nodes that draw nothing. */
export function vnodeToForme(v: VNode, ctx: TranslateContext): FormeNode | null {
  return translate(v, ctx)?.node ?? null;
}
